import { useCallback } from "react";

export interface CycleProps<T> {
  value: T;
  states: readonly T[];
  onChange: (next: T) => void;
  renderState?: (v: T, index: number) => React.ReactNode;
  size?: number;
  disabled?: boolean;
  label?: string;
}

export function Cycle<T>({
  value,
  states,
  onChange,
  renderState,
  size = 12,
  disabled = false,
  label,
}: CycleProps<T>) {
  const currentIndex = states.indexOf(value as never);
  const safeIndex = currentIndex < 0 ? 0 : currentIndex;

  const handleClick = useCallback(() => {
    const idx = states.indexOf(value as never);
    const nextIndex = ((idx < 0 ? 0 : idx) + 1) % states.length;
    onChange(states[nextIndex]);
  }, [value, states, onChange]);

  const defaultShape = useCallback(
    (_v: T, index: number): React.ReactNode => {
      const r = size / 2 - 1;
      const cx = size / 2;
      const cy = size / 2;
      if (index === 0) {
        return (
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill="transparent"
            stroke="var(--paper-rule)"
            strokeWidth={1}
          />
        );
      }
      if (index === 1) {
        return (
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill="var(--paper-ink)"
            stroke="var(--paper-ink)"
            strokeWidth={1}
          />
        );
      }
      const pad = 1;
      return (
        <polygon
          points={`${cx} ${pad},${size - pad} ${cy},${cx} ${size - pad},${pad} ${cy}`}
          fill="var(--paper-bordo)"
          stroke="var(--paper-bordo)"
          strokeWidth={1}
        />
      );
    },
    [size],
  );

  const renderFn = renderState ?? defaultShape;

  const rootStyle: React.CSSProperties = disabled
    ? { opacity: 0.5, pointerEvents: "none", cursor: "not-allowed" }
    : {};

  return (
    <div style={rootStyle}>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        onClick={handleClick}
        aria-label={label}
        role="button"
        tabIndex={0}
        style={{ cursor: "pointer", display: "block", flexShrink: 0 }}
      >
        {renderFn(value, safeIndex)}
      </svg>
    </div>
  );
}
